/**
 * SparkLayer Checkout Custom Elements Configuration
 * 
 * This file defines the custom checkout fields for SparkLayer B2B checkout.
 * Fields are dependent on each other:
 * - Location → Dates (dates update when location changes)
 * - Location → Slots (slots update when location changes)
 * - Date → Time Display (time window displayed when date is selected)
 */

// Wait for Zapiet integration to be ready
(function() {
  // Ensure zapietDeliveryDates and zapietLocationsOptions are available
  if (typeof zapietDeliveryDates === 'undefined') {
    zapietDeliveryDates = [
      {
        translations: { en: { groupname: "Loading..." } },
        items: [{ value: "", translations: { en: { label: "Loading available dates..." } } }]
      }
    ];
  }
  
  if (typeof zapietLocationsOptions === 'undefined') {
    zapietLocationsOptions = [{ translations: { en: { groupname: "Delivery location" } }, items: [] }];
  }
  
  if (typeof zapietSlotOptions === 'undefined') {
    zapietSlotOptions = [
      { translations: { en: { groupname: "Time slot" } }, items: [{ value: "", translations: { en: { label: "Select a time slot" } } }] }
    ];
  }

  // Configure SparkLayer custom checkout elements
  if (!window.sparkOptions) {
    console.error("❌ window.sparkOptions not found. Make sure SparkLayer core script is loaded first.");
    return;
  }

  console.log("SparkLayer options:", zapietDeliveryDates);
  console.log("📍 Location options for SparkLayer:", zapietLocationsOptions);
  console.log("📍 window.zapietLocations:", window.zapietLocations);

  window.sparkOptions.checkoutCustomElements = [
    ...(window.sparkOptions.checkoutCustomElements || []),

    // 1) Method selector
    {
      name: "Checkout-Method",
      translations: {
        en: {
          title: "Delivery method",
          detail: "Choose how you'd like to receive your order"
        }
      },
      attributes: {
        required: true,
        type: "select"
      },
      options: [
        {
          translations: { en: { groupname: "Options" } },
          items: [
            { value: "delivery", translations: { en: { label: "Local Delivery" } } },
            { value: "pickup", translations: { en: { label: "Store Pickup" } } }
          ]
        }
      ],
      onchange: function(val) {
        if (!val || !["delivery", "pickup"].includes(val)) {
          return { valid: false };
        }
        return { valid: true };
      }
    },

    // 2) Delivery date: use SELECT so only Zapiet-available dates are choosable
    {
      name: "Delivery-Date",
      translations: { 
        en: { 
          title: "Delivery date", 
          detail: "Choose an available delivery date (only available dates are shown)",
          "select date in future": "Please select a date today or in the future.",
          "delivery date required": "Please select a delivery date."
        } 
      },
      attributes: { 
        type: "select", 
        required: true, 
        placeholder: "Select a date"
      },
      options: zapietDeliveryDates,
      onchange: function(dateValue, element) {
        if (!dateValue) {
          if (typeof showStaticDeliveryTime === 'function') {
            showStaticDeliveryTime("");
          }
          return { valid: false, messagetranslationkey: "delivery date required" };
        }
        
        var rules = window.zapietCalendarRules;
        if (rules) {
          var todayStr = typeof getTodayDateString === 'function' ? getTodayDateString() : new Date().toISOString().split('T')[0];
          if (dateValue < todayStr) {
            return { valid: false, messagetranslationkey: "select date in future" };
          }
        }
        
        var locationId = typeof getEffectiveLocationId === 'function' ? getEffectiveLocationId() : (window.ZAPIET_DELIVERY_LOCATION_ID || null);
        if (!locationId) {
          console.warn("⚠️ No location selected, cannot fetch time slots");
          if (typeof showStaticDeliveryTime === 'function') {
            showStaticDeliveryTime("");
          }
          return { valid: false, messagetranslationkey: "Please select a location first" };
        }
        
        console.log("📅 Date changed to:", dateValue, "for location:", locationId);
        if (typeof showStaticDeliveryTime === 'function') {
          showStaticDeliveryTime("Loading...");
        }
        
        if (typeof fetchZapietTimeSlots === 'function' && typeof getTimeSlotLabelFromResponse === 'function') {
          fetchZapietTimeSlots(locationId, dateValue).then(function(timeSlotsResponse) {
            window.zapietTimeSlots = timeSlotsResponse;
            var label = getTimeSlotLabelFromResponse(timeSlotsResponse);
            if (typeof showStaticDeliveryTime === 'function') {
              showStaticDeliveryTime(label || "—");
            }
          }).catch(function(error) {
            console.error("Error fetching time slots for selected date:", error);
            if (typeof showStaticDeliveryTime === 'function') {
              showStaticDeliveryTime("");
            }
          });
        }
        
        return { valid: true };
      }
    },

    // 3) Delivery location: dropdown populated from Zapiet API
    {
      name: "Delivery-Location-Id",
      translations: { 
        en: { 
          title: "Store location", 
          detail: "Choose your delivery location" 
        } 
      },
      attributes: { 
        type: "select", 
        required: true, 
        placeholder: "Select location" 
      },
      options: zapietLocationsOptions,
      onchange: function(locationIdValue, element) {
        if (!locationIdValue) {
          // Clear dependent fields when location is cleared
          if (typeof updateDeliveryDateFieldOptions === 'function') {
            updateDeliveryDateFieldOptions([{ value: "", translations: { en: { label: "Please select a location first" } } }]);
          }
          if (typeof updateDeliverySlotFieldOptions === 'function') {
            updateDeliverySlotFieldOptions([{ value: "", translations: { en: { label: "Please select a location first" } } }]);
          }
          return { valid: false };
        }
        
        window.ZAPIET_DELIVERY_LOCATION_ID = locationIdValue;
        console.log("📍 Location changed to:", locationIdValue);
        
        // Update slot options for the new location
        if (typeof updateSlotFromLocation === 'function') {
          updateSlotFromLocation(locationIdValue);
        }
        
        // Fetch and update date options for the new location
        if (typeof refetchZapietDatesForLocation === 'function') {
          refetchZapietDatesForLocation(locationIdValue, function() {
            console.log("✅ Dates updated for location:", locationIdValue);
          });
        }
        
        return { valid: true };
      }
    },

    // 4) Delivery time slot: first available slot for the selected location
    {
      name: "Delivery-Slot-Id",
      translations: { 
        en: { 
          title: "Delivery time slot", 
          detail: "First available slot for this location" 
        } 
      },
      attributes: { 
        type: "select", 
        required: false, 
        placeholder: "Select a time slot" 
      },
      options: zapietSlotOptions,
      onchange: function(slotIdValue, element) {
        console.log("⏰ Slot changed to:", slotIdValue);
        return { valid: true };
      }
    }
  ];

  console.log("✅ SparkLayer checkout custom elements configured");
})();
